// FirebaseContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { firebaseApps } from "./firebase";
import { notification, Modal, Form, Input, Button } from "antd";
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { UserOutlined, LockOutlined } from '@ant-design/icons';

const FirebaseContext = createContext();

export const FirebaseProvider = ({ children }) => {
  // Инициализируем состояние, получая значение из localStorage (если оно есть), иначе "ColorPartner"
  const [currentProject, setCurrentProject] = useState(() => {
    return localStorage.getItem("selectedProject") || "ColorPartner";
  });

  // Состояние для отслеживания авторизации в каждом проекте
  const [authStates, setAuthStates] = useState({});
  
  // Состояние для модального окна
  const [loginModal, setLoginModal] = useState({
    visible: false,
    projectKey: null,
    loading: false
  });

  // Безопасное получение auth instance
  const getAuthSafe = (projectKey) => {
    try {
      const app = firebaseApps[projectKey];
      if (!app) {
        console.error(`Firebase app для проекта ${projectKey} не найден`);
        return null;
      }
      
      // Проверяем, является ли это объектом с свойством app или непосредственно Firebase App
      const firebaseApp = app.app || app;
      
      // Если это функция getAuth из объекта
      if (app.auth && typeof app.auth === 'function') {
        return app.auth();
      }
      
      // Иначе пытаемся получить auth стандартным способом
      return getAuth(firebaseApp);
    } catch (error) {
      console.error(`Ошибка получения auth для проекта ${projectKey}:`, error);
      return null;
    }
  };

  // Отслеживаем состояние авторизации для всех проектов
  useEffect(() => {
    const unsubscribers = {};
    
    Object.keys(firebaseApps).forEach(projectKey => {
      const auth = getAuthSafe(projectKey);
      
      if (auth) {
        try {
          unsubscribers[projectKey] = onAuthStateChanged(auth, (user) => {
            setAuthStates(prev => ({
              ...prev,
              [projectKey]: !!user
            }));
          }, (error) => {
            console.error(`Ошибка отслеживания auth для ${projectKey}:`, error);
            setAuthStates(prev => ({
              ...prev,
              [projectKey]: false
            }));
          });
        } catch (error) {
          console.error(`Не удалось подписаться на изменения auth для ${projectKey}:`, error);
        }
      } else {
        // Если auth недоступен, считаем пользователя неавторизованным
        setAuthStates(prev => ({
          ...prev,
          [projectKey]: false
        }));
      }
    });

    // Cleanup
    return () => {
      Object.values(unsubscribers).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, []);

  const switchProject = (projectKey) => {
    if (!firebaseApps[projectKey]) {
      console.error("Неверный ключ проекта:", projectKey);
      return;
    }

    // Проверяем, авторизован ли пользователь в выбранном проекте
    if (!authStates[projectKey]) {
      // Если не авторизован, показываем модальное окно
      setLoginModal({
        visible: true,
        projectKey: projectKey,
        loading: false
      });
    } else {
      // Если авторизован, переключаем проект
      performProjectSwitch(projectKey);
    }
  };

  const performProjectSwitch = (projectKey) => {
    setCurrentProject(projectKey);
    localStorage.setItem("selectedProject", projectKey);
    notification.success({
      message: `Przełączono do ${projectKey}`,
      placement: "bottomRight",
    });
  };

  const handleLogin = async (values) => {
    const { email, password } = values;
    const projectKey = loginModal.projectKey;
    
    setLoginModal(prev => ({ ...prev, loading: true }));
    
    try {
      const auth = getAuthSafe(projectKey);
      
      if (!auth) {
        throw new Error("Nie można uzyskać dostępu do systemu autoryzacji");
      }
      
      await signInWithEmailAndPassword(auth, email, password);
      
      // После успешного входа закрываем модальное окно и переключаем проект
      setLoginModal({ visible: false, projectKey: null, loading: false });
      performProjectSwitch(projectKey);
      
      notification.success({
        message: "Zalogowano pomyślnie",
        placement: "bottomRight",
      });
    } catch (error) {
      setLoginModal(prev => ({ ...prev, loading: false }));
      
      let errorMessage = "Błąd logowania";
      
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage = "Nieprawidłowy adres email";
            break;
          case 'auth/user-disabled':
            errorMessage = "Użytkownik został zablokowany";
            break;
          case 'auth/user-not-found':
            errorMessage = "Nie znaleziono użytkownika";
            break;
          case 'auth/wrong-password':
            errorMessage = "Nieprawidłowe hasło";
            break;
          case 'auth/invalid-credential':
            errorMessage = "Nieprawidłowe dane logowania";
            break;
          default:
            errorMessage = error.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      notification.error({
        message: errorMessage,
        placement: "bottomRight",
      });
    }
  };

  const handleCancelLogin = () => {
    setLoginModal({ visible: false, projectKey: null, loading: false });
  };

  // Получаем текущий Firebase объект более безопасным способом
  const getCurrentFirebase = () => {
    const firebaseObj = firebaseApps[currentProject];
    if (!firebaseObj) return null;
    
    // Если это уже готовый объект с методами (db, auth и т.д.)
    if (firebaseObj.db || firebaseObj.firestore || firebaseObj.auth) {
      return firebaseObj;
    }
    
    // Если это Firebase App instance
    return firebaseObj;
  };

  return (
    <FirebaseContext.Provider
      value={{
        currentFirebase: getCurrentFirebase(),
        currentProject,
        switchProject,
        authStates,
      }}
    >
      {children}
      
      <Modal
        title={`Zaloguj się do projektu: ${loginModal.projectKey}`}
        open={loginModal.visible}
        onCancel={handleCancelLogin}
        footer={null}
        centered
        destroyOnClose
      >
        <Form
          name="login"
          onFinish={handleLogin}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Proszę wprowadzić email!' },
              { type: 'email', message: 'Nieprawidłowy format email!' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Email" 
              size="large"
              disabled={loginModal.loading}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Hasło"
            rules={[{ required: true, message: 'Proszę wprowadzić hasło!' }]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Hasło" 
              size="large"
              disabled={loginModal.loading}
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large"
              block
              loading={loginModal.loading}
              disabled={loginModal.loading}
            >
              {loginModal.loading ? 'Logowanie...' : 'Zaloguj'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </FirebaseContext.Provider>
  );
};

export const useFirebase = () => useContext(FirebaseContext);